import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col, Spin } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import Helmet from 'react-helmet';
import {
  add,
  join,
  isNil,
  compose,
  defaultTo,
  reject,
  head,
  keys,
  eqProps,
  subtract,
  path,
  pathOr,
  multiply,
  equals,
  pick,
  sort,
  gt,
  comparator,
} from 'ramda';

import ReleaseCard from '../../components/ReleaseCard/ReleaseCard';
import ReleaseCalendar from '../../components/ReleaseCalendar/ReleaseCalendar';
import SheetTitle from '../../components/SheetTitle';
import { getLastAdded } from '../../actions/release';
import { setPageNotFound } from '../../actions/app';
import { getReleaseDates } from '../../actions/calendar';
import { releaseByDateList as releaseByDateListSelector, releaseDatesSelector } from '../../selectors';
import Pagination, { getReleaseList, getPageSize } from '../../components/Pagination';
import { isSSR, smoothScrollTo, smoothScroll } from '../../helpers/view';
import analytics from '../../helpers/analytics';
import Error from '../../components/Error';

import c from './LastAdded.less';

const QUERY_DATE_FORMAT = 'YYYY-M-D';
const SERVER_DATE_FORMAT = 'YYYY-MM-DD';

const dateKeys = ['year', 'month', 'day'];
const dateComparator = comparator((a, b) => gt(a, b));

function serverToQueryDateFormat(input) {
  return moment(input, SERVER_DATE_FORMAT).format(QUERY_DATE_FORMAT);
}

class LastAddedContainer extends Component {
  static _formatDate(date) {
    return moment(date, QUERY_DATE_FORMAT).format(SERVER_DATE_FORMAT);
  }

  static propTypes = {
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    artistList: PropTypes.object.isRequired,
    releaseByDateList: PropTypes.shape({
      total: PropTypes.number,
      ids: PropTypes.array,
    }).isRequired,
    releaseList: PropTypes.object.isRequired,
    getLastAdded: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    releasesByDate: PropTypes.object.isRequired,
    app: PropTypes.object.isRequired,
    releaseDates: PropTypes.array.isRequired,
    getReleaseDates: PropTypes.func.isRequired,
    setPageNotFound: PropTypes.func.isRequired,
  };

  state = {
    selectedDate: moment(),
  };

  UNSAFE_componentWillMount() {
    const { match: { params }, releaseByDateList: { total } } = this.props;

    if (isSSR()) {
      if (total === 0 || total < (parseInt(params.page, 10) - 1) * getPageSize()) {
        this.props.staticContext.page.status = 404;
      }
    }

    if (params.year && params.month && params.day) {
      this.setState({
        selectedDate: moment(`${params.year}-${params.month}-${params.day}`, 'YYYY-M-D'),
      });
    } else if (this.props.releaseDates.length > 0) {
      this.setState({
        selectedDate: this.props.releaseDates[this.props.releaseDates.length - 1],
      });
    }
  }

  componentDidMount() {
    this._browserRenderedAction();

    smoothScroll();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!eqProps('page', this.props.match.params, nextProps.match.params)) {
      const params = path(['match', 'params'], nextProps);

      this._loadData(
        params,
        multiply(getPageSize(), subtract(defaultTo(1, params.page), 1)),
        path(['state', 'loadMore'], nextProps.location),
      );

      if (equals(pick(dateKeys, params), pick(dateKeys, path(['match', 'params'], this.props)))) {
        if (params.year && params.month && params.day) {
          analytics.pageview(`/catalog/last-added/${params.year}-${params.month}-${params.day}`);
        } else {
          analytics.pageview('/catalog/last-added');
        }
      }

      return;
    }

    if (
      !eqProps('year', this.props.match.params, nextProps.match.params)
      || !eqProps('month', this.props.match.params, nextProps.match.params)
      || !eqProps('day', this.props.match.params, nextProps.match.params)
    ) {
      const params = path(['match', 'params'], nextProps);

      this._loadData(params);
    }

    if (!eqProps('releaseByDateList', nextProps, this.props)) {
      const { match: { params: { page } }, releaseByDateList: { total } } = nextProps;

      if (total < (parseInt(page, 10) - 1) * getPageSize()) {
        this.props.setPageNotFound();
      }
    }

    if (!this.state.selectedDate && nextProps.releaseDates.length > 0) {
      this.setState({
        selectedDate: nextProps.releaseDates[nextProps.releaseDates.length - 1],
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { match: { params: { page: prevPage } } } = prevProps;
    const { match: { params: { page: currentPage } } } = this.props;
    const loadMore = path(['state', 'loadMore'], this.props.location);

    if (prevPage !== currentPage) {
      if (!loadMore) {
        smoothScrollTo(`.${c.rowWrapper}`);
      }
    }
  }

  get queryDate() {
    const { match: { params } } = this.props;

    if (params.year && params.month && params.day) {
      return moment(`${params.year}-${params.month}-${params.day}`, QUERY_DATE_FORMAT);
    }

    return null;
  }

  getBaseHref = () => {
    const params = path(['match', 'params'], this.props);

    if (params.year && params.month && params.day) {
      return `/last-added/${params.year}-${params.month}-${params.day}`;
    }

    return '/last-added';
  };

  handleLoadMore = () => {
    this._buildUrl(compose(
      add(1),
      defaultTo(1),
      path(['match', 'params', 'page']),
    )(this.props), true);
  };

  handleSelectDate = (selectedDate) => {
    if (!selectedDate.isSame(this.state.selectedDate) && !selectedDate.isSame(this.queryDate)) {
      this.setState({
        selectedDate,
      }, () => {
        if (!this.queryDate && this.props.releaseDates.find((date) => date.isSame(selectedDate))) {
          return;
        }

        this.props.history.push(`/last-added/${selectedDate.format(QUERY_DATE_FORMAT)}`);
      });
    }
  };

  handlePageChange = (page) => {
    this._buildUrl(page);
  };

  _buildUrl = (page, loadMore = false) => {
    const { match: { params }, releasesByDate: { ids: releasesByDateIds } } = this.props;

    this.props.history.push({
      pathname: compose(
        join('/'),
        reject(isNil),
      )([
        '',
        'last-added',
        params.year && params.month && params.day
          ? `${params.year}-${params.month}-${params.day}`
          : serverToQueryDateFormat(head(sort(dateComparator, keys(releasesByDateIds)))),
        page,
      ]),
      state: {
        loadMore,
      },
    });
  };

  _browserRenderedAction() {
    if (this.props.app.renderedComponent === 'LastAdded') return;

    this.props.getReleaseDates();

    const { match: { params } } = this.props;

    this._loadData(
      params,
      multiply(getPageSize(), subtract(pathOr(1, ['match', 'params', 'page'], this.props), 1)),
    );
  }

  _loadData(params, offset = 0, concat = false) {
    this.props.getLastAdded({
      ...(params.year && params.month && params.day ? {
        date: LastAddedContainer._formatDate(`${params.year}-${params.month}-${params.day}`),
      } : {}),
      limit: getPageSize(),
      offset,
      concat,
    });
  }

  render() {
    const {
      releaseList, match, releaseByDateList, artistList, releaseDates,
    } = this.props;
    const { selectedDate } = this.state;
    const { page } = match.params;

    if (
      !releaseList.loading
      && (releaseByDateList.total === 0 || releaseByDateList.total < (parseInt(page, 10) - 1) * getPageSize())
    ) {
      return <Error errorCode="404" />;
    }

    return (
      <div className={c.container}>
        <Helmet defer={false}>
          <title>Mp3Caprice - Last Added Music</title>
          <meta
            name="description"
            content="New releases at Mp3Caprice. Discover, buy and download fresh EPs, singles and albums."
          />
          <meta property="mc:dp" content={`/catalog${this.getBaseHref()}`} />
          <meta property="mc:page" content="LastAdded" />
        </Helmet>
        <div className={c.headerBlock}>
          <ReleaseCalendar
            releaseDates={releaseDates}
            defaultDate={this.queryDate}
            onDateSelect={this.handleSelectDate}
            title="Last added"
          />
        </div>
        <SheetTitle title={selectedDate.format('DD MMMM YYYY')} />
        <Spin spinning={releaseList.loading} size="large">
          <div className={c.rowWrapper}>
            <Row
              gutter={{
                xs: 10, sm: 16, md: 20, lg: 20,
              }}
              className={c.releaseListContainer}
            >
              {
                getReleaseList(releaseByDateList.ids, releaseList, artistList).map((release) => (
                  <Col xs={12} sm={12} md={8} lg={6} key={release.id}>
                    <ReleaseCard release={release} />
                  </Col>
                ))
              }
            </Row>
          </div>
        </Spin>
        <Pagination
          total={releaseByDateList.total}
          currentPage={page}
          isLoading={releaseList.loading}
          onLoadMore={this.handleLoadMore}
          onPageChange={this.handlePageChange}
          baseHref={this.getBaseHref()}
        />
      </div>
    );
  }
}

const mapStateToProps = (store, props) => ({
  app: store.app,
  artistList: store.artistList,
  releaseByDateList: releaseByDateListSelector(store, props),
  releaseList: store.releases.list,
  releasesByDate: store.releases.byDate,
  releaseDates: releaseDatesSelector(store),
});

const mapDispatchToProps = {
  getLastAdded,
  getReleaseDates,
  setPageNotFound,
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(LastAddedContainer);
